
import { useState, useEffect } from 'react';
import { useUser, useFirestore, useDoc, useMemoFirebase, updateDocumentNonBlocking } from '@/firebase';
import { doc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { Heart, Loader2 } from 'lucide-react';
import type { UserProfile } from '@/app/lib/types';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

interface FavoriteButtonProps {
  locationId: string;
}

export function FavoriteButton({ locationId }: FavoriteButtonProps) {
  const { user, isUserLoading } = useUser();
  const firestore = useFirestore();
  const { toast } = useToast();
  const navigate = useNavigate();

  const userProfileRef = useMemoFirebase(() => {
    if (!user) return null;
    return doc(firestore, 'users', user.uid);
  }, [user, firestore]);

  const { data: userProfile, isLoading: isProfileLoading } = useDoc<UserProfile>(userProfileRef);

  const [isFavorited, setIsFavorited] = useState(false);

  useEffect(() => {
    if (userProfile?.favoriteSpots) {
      setIsFavorited(userProfile.favoriteSpots.includes(locationId));
    }
  }, [userProfile, locationId]);

  const handleToggleFavorite = () => {
    if (!user || !userProfileRef) {
      navigate('/login');
      return;
    }

    const newIsFavorited = !isFavorited;
    setIsFavorited(newIsFavorited); // Optimistic UI update

    const updateData = {
      favoriteSpots: newIsFavorited ? arrayUnion(locationId) : arrayRemove(locationId),
    };

    updateDocumentNonBlocking(userProfileRef, updateData);

    toast({
      title: newIsFavorited ? 'Added to Favorites!' : 'Removed from Favorites',
    });
  };
  
  const isLoading = isUserLoading || isProfileLoading;

  if (!user && !isUserLoading) {
    return (
       <Button variant="outline" onClick={() => navigate('/login')}>
        <Heart className="mr-2 h-4 w-4" />
        Favorite
      </Button>
    )
  }

  return (
    <Button variant="outline" onClick={handleToggleFavorite} disabled={isLoading}>
      {isLoading ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <Heart
          className={cn(
            'mr-2 h-4 w-4',
            isFavorited && 'fill-accent text-accent'
          )}
        />
      )}
      {isFavorited ? 'Favorited' : 'Favorite'}
    </Button>
  );
}
